import React from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faCloud,
  faSun,
  faSmog,
  faCloudRain,
  faThunderstorm,
  faSnowflake,
  faCloudSun,
  faWind
} from "@fortawesome/free-solid-svg-icons";

// Components
import Help from "./Help.js";

// Utils
import {slugify, toTitleCase} from "../utils/helpers";
import {LIVE_WEATHER_SOURCE_TITLE} from "../utils/weather-links.js";

// Style
import {voice} from "../style/components/typography";
import {flex} from "../style/components/mixins";
import {colors, pad, radius} from "../style/components/variables";
import {Small, Text} from "../style/components/general";

const getWeatherIcon = description => {
  if (description === "Partly Cloudy") return faCloudSun;
  if (description === "Cloudy") return faCloud;
  if (description === "Sunny") return faSun;
  if (description === "Fog") return faSmog;
  if (description === "Rain") return faCloudRain;
  if (description === "Thunderstorm") return faThunderstorm;
  if (description === "Snow" || description === "Hail") return faSnowflake;
  if (description === "Windy") return faWind;
  return null;
};

const Weather = React.forwardRef(({weather, links = []}, ref) => {
  const wind = [weather.windDirection, weather.windSpeed].filter(Boolean).join(" ");

  const observed = [
    weather?.rainfall > 0 && {label: "Rainfall", value: `${weather.rainfall} in.`},
    weather?.cumulative > 0 && {label: "Cumulative", value: `${weather.cumulative} in.`}
  ].filter(Boolean);

  const forecast = [
    weather.precipitationChance != null && {
      label: "Precip Chance",
      value: `${Math.round(weather.precipitationChance)}%`
    },
    wind && {label: "Wind", value: wind}
  ].filter(Boolean);

  const groups = [
    {
      title: "Observed",
      help:
        "Measured at the nearest weather station for this record's date. Some stations report the 24 hours ending at their morning reading, not midnight to midnight. Cumulative is the total since rain started, back to the last dry day. Values appear the morning after the record's date.",
      rows: observed
    },
    {
      title: "Today's Forecast",
      help: "National Weather Service daytime forecast (about 6am–6pm), as of the 6am update. Wind is the prevailing direction and speed range for that period.",
      rows: forecast
    }
  ].filter(({rows}) => rows.length > 0);

  return (
    <WeatherBlock ref={ref}>
      <WeatherWrapper>
        <WeatherDetails>
          {weather.description && (
            <WeatherDescription>
              <FontAwesomeIcon icon={getWeatherIcon(weather.description)} />
              <Text>{toTitleCase(weather.description)}</Text>
            </WeatherDescription>
          )}
          {weather.tempAvg != null && (
            <WeatherAttrContainer>
              <WeatherAttrLabel>Avg</WeatherAttrLabel>
              <Text>{weather.tempAvg} ˚F</Text>
            </WeatherAttrContainer>
          )}
          {weather.tempMax != null && weather.tempMin != null && (
            <HighLowContainer>
              <TempContainer high={1}>{weather.tempMax}˚</TempContainer>
              <TempContainer>{weather.tempMin}˚</TempContainer>
            </HighLowContainer>
          )}
        </WeatherDetails>
        {groups.length > 0 && (
          <WeatherGroups>
            {groups.map(({title, help, rows}) => (
              <div key={title}>
                <WeatherGroupTitle>
                  {title}
                  <Help left>{help}</Help>
                </WeatherGroupTitle>
                {rows.map(({label, value}) => (
                  <WeatherRow key={label}>
                    <WeatherAttrLabel>{label}</WeatherAttrLabel>
                    <Text>{value}</Text>
                  </WeatherRow>
                ))}
              </div>
            ))}
          </WeatherGroups>
        )}
      </WeatherWrapper>
    {links.length > 0 && (
      <SourceLinks>
        <Small secondary>Source:&nbsp;</Small>
        {links.map(({label, url}, index) => (
          <React.Fragment key={label}>
            {index > 0 && <Small secondary>,&nbsp;</Small>}
            <SourceLink
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title={LIVE_WEATHER_SOURCE_TITLE}
              data-testid={`weather-source-${slugify(label)}`}>
              {label} ↗
            </SourceLink>
          </React.Fragment>
          ))}
        </SourceLinks>
      )}
    </WeatherBlock>
  );
});

Weather.displayName = "Weather";

Weather.propTypes = {
  weather: PropTypes.objectOf(PropTypes.any).isRequired,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      provider: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })
  )
};

// Style Overrides
const WeatherBlock = styled.div`
  margin-bottom: ${pad}px;
`;

const WeatherWrapper = styled.div`
  border: 2px solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  padding: ${pad}px;
`;

const WeatherDetails = styled.div`
  ${flex("row", "wrap", "space-around", "center")}
  gap: ${pad / 2}px ${pad}px;

  ${voice.normal}
  font-weight: bold;
`;

const TempContainer = styled.div`
  font-weight: bold;
  ${({high, theme}) =>
    high
      ? css`
          border-bottom: 1px solid ${theme.secondary};
          padding-bottom: ${pad / 3}px;
          color: ${colors.orange};
        `
      : css`
          padding-top: ${pad / 3}px;
          color: ${colors.blueLight};
        `}
`;

const WeatherAttrLabel = styled.div`
  font-size: 11px;
  font-weight: normal;
  color: ${({theme}) => theme.secondary};
`;

const HighLowContainer = styled.div`
  ${voice.quiet}
  gap: 0;
  display: block;

  div {
    text-align: center;
  }
`;

const WeatherDescription = styled.div`
  margin-top: -1px;
  ${voice.small}
  ${flex("column", "nowrap", "center", "center")}
  gap: ${pad / 2}px;
  color: ${({theme}) => theme.primary};

  svg {
    ${voice.medium}
    fill: ${({theme}) => theme.primary};
  }
`;

const WeatherAttrContainer = styled.div`
  ${voice.small}
  ${flex("column", "nowrap", "start", "center")}
  gap: ${pad / 2}px;
  white-space: nowrap;
`;

const WeatherGroups = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${pad / 2}px ${pad * 2}px;
  margin-top: ${pad}px;
  padding-top: ${pad}px;
  border-top: 1px solid ${({theme}) => theme.secondary};
`;

const WeatherGroupTitle = styled.div`
  ${flex("row", "nowrap", "start", "center")}
  gap: ${pad / 2}px;
  ${voice.quiet}
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({theme}) => theme.secondary};
  margin-bottom: ${pad / 4}px;
`;

const WeatherRow = styled.div`
  ${flex("row", "nowrap", "space-between", "baseline")}
  gap: ${pad}px;
  white-space: nowrap;

  p {
    ${voice.small}
    font-weight: bold;
  }
`;

const SourceLinks = styled.div`
  ${flex("row", "wrap", "start", "center")};
  margin-top: ${pad / 2}px;
`;

const SourceLink = styled.a`
  ${voice.quiet};
  color: ${({theme}) => theme.secondary};
  text-decoration: underline;
`;

export default Weather;
